import { useMemo, useState } from "react";
import { Plus, UsersRound } from "lucide-react";

import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";
import ContentItem from "../../../../components/layout/content/ContentItem";

import SecondaryPillTabs from "../../../../components/tabs/SecondaryPillTabs";
import SearchField from "../../../../components/input/searchField/SearchField";
import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";
import FormModal from "../../../../components/modals/FormModal";
import RefreshButton from "../../../../components/buttons/refresh/RefreshButton";
import { EllipsisMenu } from "../../../../components/menu/ellipsis/EllipsisMenu";

import CreatorAccessForm from "../forms/CreatorAccessForm";

import "./CreatorAccessSection.css";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "group", label: "Groups" },
  { id: "user", label: "Individuals" },
];

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.response?.data?.message ||
      error?.message ||
      "Unable to manage access.";

const toDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) return value;

  const text = String(value).trim().replace(" ", "T");
  const iso = /(?:Z|[+-]\d\d:?\d\d)$/i.test(text) ? text : `${text}Z`;

  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatExpiry = (value) =>
  toDate(value)?.toLocaleString() || "Never expires";

export function CreatorAccessSection({ resource, config }) {
  const {
    grants = [],
    loading,
    saving,
    revokingId,
    error,
    notice,
    revokeGrant,
    clearFeedback,
  } = resource;

  const [selectedGrant, setSelectedGrant] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("all");

  const busy = loading || saving || revokingId != null;

  const filteredGrants = useMemo(() => {
    const term = search.trim().toLowerCase();

    return grants.filter((grant) => {
      const label =
        grant.recipientType === "group"
          ? grant.groupName || grant.groupId
          : grant.userEmail || grant.userId;

      return (
        (recipientFilter === "all" ||
          recipientFilter === grant.recipientType) &&
        (!term ||
          String(label || "")
            .toLowerCase()
            .includes(term))
      );
    });
  }, [grants, search, recipientFilter]);

  const tabs = FILTERS.map((filter) => ({
    ...filter,
    label: `${filter.label} (${
      filter.id === "all"
        ? grants.length
        : grants.filter((grant) => grant.recipientType === filter.id).length
    })`,
  }));

  const openEditor = (grant = null) => {
    if (busy) return;

    clearFeedback?.();
    setSelectedGrant(grant);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (saving) return;

    setEditorOpen(false);
    setSelectedGrant(null);
  };

  const handleTabChange = (selected) => {
    const value =
      typeof selected === "string" ? selected : selected?.id || selected?.value;

    if (FILTERS.some((item) => item.id === value)) {
      setRecipientFilter(value);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(typeof value === "string" ? value : value?.target?.value || "");
  };

  const confirmRevoke = async (grant) => {
    if (busy) return;

    if (
      !window.confirm(
        `Revoke ${config.label.toLowerCase()} access for this recipient?`,
      )
    ) {
      return;
    }

    try {
      await revokeGrant(grant.grantId);
    } catch (error) {
      console.error("Unable to revoke access:", getErrorMessage(error));
    }
  };

  return (
    <div className="creator-access-section">
      <ContentPanelHeader
        title={`${config.label} Access`}
        subTitle="Manage access for users and groups."
      >
        <Button
          value="Grant Access"
          icon={<Plus size={16} />}
          variant="primary-outlined"
          disabled={busy}
          onClick={() => openEditor()}
        />
      </ContentPanelHeader>

      {error && (
        <p className="creator-access-section__error" role="alert">
          {getErrorMessage(error)}
        </p>
      )}

      {notice && (
        <p className="creator-access-section__notice" role="status">
          {notice}
        </p>
      )}

      <SecondaryPillTabs
        tabs={tabs}
        activeTab={recipientFilter}
        onChange={handleTabChange}
      />

      <br />

      <SearchField
        placeholder="Search users or groups..."
        value={search}
        onChange={handleSearchChange}
      />

      <br />

      {loading ? (
        <p className="creator-access-section__loading" role="status">
          Loading access grants...
        </p>
      ) : !filteredGrants.length ? (
        <EmptyState
          icon={<UsersRound size={28} />}
          title={grants.length ? "No matching grants" : "No access grants yet"}
          description={
            grants.length
              ? "Try another search or filter."
              : "Grant access to users or groups to get started."
          }
        />
      ) : (
        <div className="creator-access-section__list">
          {filteredGrants.map((grant) => {
            const count = (grant[config.grantIdsKey] || []).length;

            const label =
              grant.recipientType === "group"
                ? grant.groupName || `Group #${grant.groupId}`
                : grant.userEmail || `User #${grant.userId}`;

            const options = [
              {
                label:
                  grant.status === "revoked"
                    ? "Reactivate Access"
                    : "Edit Access",
                action: () => openEditor(grant),
              },
              ...(grant.status !== "revoked"
                ? [
                    {
                      label: "Revoke Access",
                      variant: "danger",
                      disabled: busy,
                      action: () => confirmRevoke(grant),
                    },
                  ]
                : []),
            ];

            return (
              <ContentItem
                key={grant.grantId}
                className="creator-access-section__item"
                actions={
                  <EllipsisMenu
                    position="right"
                    disabled={busy}
                    options={options}
                  />
                }
              >
                <div className="creator-access-section__info">
                  <div className="creator-access-section__identity">
                    <strong>{label}</strong>

                    <span
                      className={`creator-access-section__status creator-access-section__status--${grant.status || "unknown"}`}
                    >
                      {grant.status || "unknown"}
                    </span>
                  </div>

                  <span className="creator-access-section__meta">
                    {grant.accessScope === "all"
                      ? `All eligible ${config.label.toLowerCase()}`
                      : `${count} selected ${config.label.toLowerCase()}`}
                  </span>

                  <span className="creator-access-section__meta">
                    {formatExpiry(grant.expiresAt)}
                  </span>
                </div>
              </ContentItem>
            );
          })}
        </div>
      )}

      {editorOpen && (
        <CreatorAccessForm
          key={selectedGrant?.grantId ?? "new"}
          resource={resource}
          config={config}
          grant={selectedGrant}
          onClose={closeEditor}
        />
      )}
    </div>
  );
}

export default CreatorAccessSection;
